import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import './DeviceDetail.css';
import './DeviceList.css';
import './DeviceGroups.css';
import './AlarmruleDetail.css';
import { API_BASE, authHeaders } from '../constants/api';
import { severityLabels, SEVERITY_OPTIONS, entityTypeLabels, getSeverities } from '../constants/alarmConstants';
import { AlarmStatusDropdown } from '../UI-Elements';
import { formatDateDE } from '../utils/formatDate';
import { useAuth } from '../context/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import Pagination from './Pagination';
import ErrorBoundary from './ErrorBoundary';
import { Zurück, Bearbeiten, Löschen, Speichern, Abbrechen } from '../icons';
import { SearchDropdown } from '../UI-Elements';

const HISTORY_PAGE_SIZE = 5;

function getConditionInfo(rule) {
  const createRules = rule?.configuration?.createRules || {};
  const results = [];
  for (const [, ruleBody] of Object.entries(createRules)) {
    const filters = ruleBody?.condition?.expression?.filters || [];
    for (const filter of filters) {
      const key = filter.argument;
      const vType = filter.valueType;
      const preds = filter.predicates || [];
      const filterOp = filter.operation; // 'AND' (INSIDE) | 'OR' (OUTSIDE/single)

      const isNumericFilter = vType === 'NUMERIC' || preds.some(p => p.type === 'NUMERIC');
      const isBoolFilter = vType === 'BOOLEAN' || preds.some(p => p.type === 'BOOLEAN');
      const isStringFilter = vType === 'STRING' || preds.some(p => p.type === 'STRING');

      if (isBoolFilter) {
        const val = preds[0]?.value?.staticValue;
        results.push({ key, type: 'bool', label: val ? 'Wahr' : 'Falsch' });
      } else if (isStringFilter) {
        const val = preds[0]?.value?.staticValue;
        results.push({ key, type: 'string', label: `= "${val}"` });
      } else if (isNumericFilter) {
        const fmtPred = (p) => {
          const v = p.value?.staticValue;
          const o = p.operation;
          if (o === 'LESS_OR_EQUAL') return `≤ ${v}`;
          if (o === 'GREATER_OR_EQUAL') return `≥ ${v}`;
          if (o === 'LESS') return `< ${v}`;
          if (o === 'GREATER') return `> ${v}`;
          if (o === 'EQUAL') return `= ${v}`;
          return `${o} ${v}`;
        };
        if (preds.length === 1) {
          results.push({ key, type: 'numeric', label: fmtPred(preds[0]) });
        } else if (preds.length >= 2) {
          const sorted = [...preds].sort(
            (a, b) => Number(a.value?.staticValue) - Number(b.value?.staticValue)
          );
          const conjunction = filterOp === 'AND' ? ' und ' : ' oder ';
          results.push({ key, type: 'numeric', label: sorted.map(fmtPred).join(conjunction) });
        }
      }
    }
  }
  return results;
}

function AlarmruleDetail() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const { authToken: token } = useAuth();
  const titleRef = useRef(null);

  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [entityDisplayName, setEntityDisplayName] = useState('');

  const [historyPage, setHistoryPage] = useState(0);
  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteConfirmRef = useRef(null);
  useFocusTrap(showDeleteConfirm, deleteConfirmRef);

  const blockerModalRef = useRef(null);

  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [showAlarmDeleteConfirm, setShowAlarmDeleteConfirm] = useState(false);
  const [deletingAlarm, setDeletingAlarm] = useState(false);
  const alarmDeleteConfirmRef = useRef(null);
  useFocusTrap(showAlarmDeleteConfirm, alarmDeleteConfirmRef);

  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const showStatus = (msg, type = 'success') => {
    setStatusType(type);
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), 8000);
  };

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editSeverity, setEditSeverity] = useState('');
  const [editDataKeys, setEditDataKeys] = useState([]);
  const [editSelectedKey, setEditSelectedKey] = useState('');
  const [editSeverityText, setEditSeverityText] = useState('');
  const [editKeyText, setEditKeyText] = useState('');
  const [editCondition, setEditCondition] = useState('ABOVE');
  const [editThreshold, setEditThreshold] = useState('');
  const [editLowerThreshold, setEditLowerThreshold] = useState('');
  const [editUpperThreshold, setEditUpperThreshold] = useState('');
  const [editStateValue, setEditStateValue] = useState('');
  const [saving, setSaving] = useState(false);
  const editOriginalRef = useRef(null);

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  useEffect(() => {
    if (!ruleId || !token) return;
    async function loadRule() {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE}/calculatedField/${ruleId}`, {
          headers: authHeaders(token),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht geladen werden (HTTP ${res.status})`);
        }
        const data = await res.json();
        setRule(data);
      } catch (err) {
        setError(err.message || 'Fehler beim Laden der Alarmregel. Bitte prüfen Sie Ihre Netzwerkverbindung.');
      } finally {
        setLoading(false);
      }
    }
    loadRule();
  }, [ruleId, token]);


  useEffect(() => {
    if (!rule || !token) return;
    const entityType = rule.entityId?.entityType;
    const entityId = rule.entityId?.id;
    if (!entityId) return;
    if (rule.entityName) { setEntityDisplayName(rule.entityName); return; }
    async function fetchEntityName() {
      try {
        let url = '';
        if (entityType === 'DEVICE') url = `${API_BASE}/device/${entityId}`;
        else if (entityType === 'DEVICE_PROFILE') url = `${API_BASE}/deviceProfile/${entityId}`;
        else if (entityType === 'ASSET') url = `${API_BASE}/asset/${entityId}`;
        else if (entityType === 'ASSET_PROFILE') url = `${API_BASE}/assetProfile/${entityId}`;
        else return;
        const res = await fetch(url, { headers: authHeaders(token) });
        if (!res.ok) return;
        const data = await res.json();
        if (data.name) setEntityDisplayName(data.name);
      } catch { /* ignore */ }
    }
    fetchEntityName();
  }, [rule, token]);

  useEffect(() => {
    if (!rule || !token) return;
    async function loadHistory() {
      try {
        setHistoryLoading(true);
        const entityType = rule.entityId?.entityType;
        const entityId = rule.entityId?.id;
        if (!entityType || !entityId) {
          setHistory([]);
          return;
        }
        const params = new URLSearchParams({
          entityType,
          entityId,
          pageSize: '20',
          page: '0',
          sortProperty: 'createdTime',
          sortOrder: 'DESC',
        });
        const res = await fetch(`${API_BASE}/alarms?${params.toString()}`, {
          headers: authHeaders(token),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Alarmhistorie (HTTP ${res.status})`);
        }
        const data = await res.json();
        // Filter alarms that match this rule's alarm type
        const alarmType = rule.name;
        const filtered = (data.data || []).filter(a => a.type === alarmType);
        setHistory(filtered);
      } catch (err) {
        console.error('Alarmhistorie konnte nicht geladen werden:', err);
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    }
    loadHistory();
  }, [rule, token]);

  useEffect(() => {
    if (!loading && rule && titleRef.current) {
      try { titleRef.current.focus(); } catch {}
    }
  }, [loading, rule]);

  async function deleteRule() {
    try {
      setDeleting(true);
      const res = await fetch(`${API_BASE}/calculatedField/${ruleId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      navigate('/alarm-rules', {
        state: { statusMessage: `Alarmregel „${rule?.name}" wurde erfolgreich gelöscht.` },
      });
    } catch (err) {
      showStatus(`Alarmregel konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
      setShowDeleteConfirm(false);
      setDeleting(false);
    }
  }

  async function updateAlarmStatus(alarm, action, newStatus) {
    try {
      await updateAlarmStatusAPI(alarm.id.id, action, token);
      setHistory(prev => prev.map(a => a.id.id === alarm.id.id ? { ...a, status: newStatus } : a));
      setOpenAlarmStatusMenu(null);
      const labels = { ACTIVE_ACK: 'Alarm bestätigt', CLEARED_ACK: 'Alarm behoben' };
      showStatus(labels[newStatus] || 'Status geändert');
    } catch (err) {
      showStatus(`Alarmstatus konnte nicht geändert werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    }
  }

  async function confirmDeleteAlarm() {
    if (!alarmToDelete) return;
    try {
      setDeletingAlarm(true);
      const res = await fetch(`${API_BASE}/alarm/${alarmToDelete.id.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarm konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setHistory(prev => {
        const updated = prev.filter(a => a.id.id !== alarmToDelete.id.id);
        const newTotalPages = Math.max(1, Math.ceil(updated.length / HISTORY_PAGE_SIZE));
        if (historyPage >= newTotalPages) setHistoryPage(newTotalPages - 1);
        return updated;
      });
      showStatus('Alarm gelöscht.');
    } catch (err) {
      showStatus(`Alarm konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeletingAlarm(false);
      setShowAlarmDeleteConfirm(false);
      setAlarmToDelete(null);
    }
  }

  function startEditing() {
    if (!rule) return;
    const createRules = rule?.configuration?.createRules || {};
    const severityKeys = Object.keys(createRules);
    setEditName(rule.name || '');
    const firstSeverity = severityKeys[0] || '';
    setEditSeverity(firstSeverity);
    const sevOpt = SEVERITY_OPTIONS.find(o => o.value === firstSeverity);
    setEditSeverityText(sevOpt?.label || firstSeverity);

    // Extract values directly from rule configuration
    let foundKey = '';
    let foundCondition = 'ABOVE';
    let foundThreshold = '';
    let foundLower = '';
    let foundUpper = '';
    let foundStateValue = '';
    for (const [, ruleBody] of Object.entries(createRules)) {
      const filters = ruleBody?.condition?.expression?.filters || [];
      for (const filter of filters) {
        foundKey = filter.argument || foundKey;
        const vType = filter.valueType;
        const preds = filter.predicates || [];
        if (vType === 'STRING' || preds.some(p => p.type === 'STRING')) {
          foundCondition = 'STATE';
          foundStateValue = String(preds[0]?.value?.staticValue ?? '');
        } else if (vType === 'NUMERIC' || preds.some(p => p.type === 'NUMERIC')) {
          const filterOp = filter.operation || 'OR';
          if (preds.length === 1) {
            const pred = preds[0];
            const val = String(pred.value?.staticValue ?? '');
            if (pred.operation === 'GREATER_OR_EQUAL' || pred.operation === 'GREATER') {
              foundCondition = 'ABOVE'; foundThreshold = val;
            } else {
              foundCondition = 'BELOW'; foundThreshold = val;
            }
          } else if (preds.length >= 2) {
            const leq = preds.find(p => p.operation === 'LESS_OR_EQUAL' || p.operation === 'LESS');
            const geq = preds.find(p => p.operation === 'GREATER_OR_EQUAL' || p.operation === 'GREATER');
            if (filterOp === 'AND') {
              foundCondition = 'INSIDE';
              foundLower = String(geq?.value?.staticValue ?? '');
              foundUpper = String(leq?.value?.staticValue ?? '');
            } else {
              foundCondition = 'OUTSIDE';
              foundLower = String(leq?.value?.staticValue ?? '');
              foundUpper = String(geq?.value?.staticValue ?? '');
            }
          }
        }
      }
    }
    setEditSelectedKey(foundKey);
    setEditKeyText(foundKey);
    setEditCondition(foundCondition);
    setEditThreshold(foundThreshold);
    setEditLowerThreshold(foundLower);
    setEditUpperThreshold(foundUpper);
    setEditStateValue(foundStateValue);
    editOriginalRef.current = { name: rule.name || '', severity: severityKeys[0] || '', key: foundKey, condition: foundCondition, threshold: foundThreshold, lower: foundLower, upper: foundUpper, stateValue: foundStateValue };
    // Load data keys for entity
    loadEditDataKeys();
    setEditing(true);
  }

  async function loadEditDataKeys() {
    if (!rule || !token) return;
    try {
      const entityType = rule.entityId?.entityType;
      const entityId = rule.entityId?.id;
      if (entityType === 'DEVICE' && entityId) {
        const res = await fetch(`${API_BASE}/plugins/telemetry/DEVICE/${entityId}/keys/timeseries`, { headers: authHeaders(token) });
        if (res.ok) { const keys = await res.json(); setEditDataKeys(keys || []); }
      } else if (entityType === 'DEVICE_PROFILE' && entityId) {
        const devRes = await fetch(`${API_BASE}/tenant/devices?pageSize=1&page=0&deviceProfileId=${entityId}`, { headers: authHeaders(token) });
        if (!devRes.ok) return;
        const devData = await devRes.json();
        const firstDevice = (devData.data || [])[0];
        if (!firstDevice) return;
        const res = await fetch(`${API_BASE}/plugins/telemetry/DEVICE/${firstDevice.id.id}/keys/timeseries`, { headers: authHeaders(token) });
        if (res.ok) { const keys = await res.json(); setEditDataKeys(keys || []); }
      }
    } catch { /* ignore */ }
  }

  function cancelEditing() {
    setEditing(false);
  }

  async function saveEdit() {
    if (!editIsValid) return;
    const predicates = [];
    if (editCondition === 'ABOVE') {
      predicates.push({ type: 'NUMERIC', operation: 'GREATER_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editThreshold) } });
    } else if (editCondition === 'BELOW') {
      predicates.push({ type: 'NUMERIC', operation: 'LESS_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editThreshold) } });
    } else if (editCondition === 'OUTSIDE') {
      predicates.push({ type: 'NUMERIC', operation: 'LESS_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editLowerThreshold) } });
      predicates.push({ type: 'NUMERIC', operation: 'GREATER_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editUpperThreshold) } });
    } else if (editCondition === 'INSIDE') {
      predicates.push({ type: 'NUMERIC', operation: 'GREATER_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editLowerThreshold) } });
      predicates.push({ type: 'NUMERIC', operation: 'LESS_OR_EQUAL', ignoreCase: false, predicates: null, value: { staticValue: Number(editUpperThreshold) } });
    } else if (editCondition === 'STATE') {
      const trimmed = editStateValue.trim();
      const isNumericState = trimmed !== '' && !isNaN(Number(trimmed));
      predicates.push({ type: isNumericState ? 'NUMERIC' : 'STRING', operation: 'EQUAL', ignoreCase: !isNumericState, predicates: null, value: { staticValue: isNumericState ? Number(trimmed) : trimmed } });
    }

    const saveValueType = editCondition === 'STATE'
      ? (() => { const t = editStateValue.trim(); return (t !== '' && !isNaN(Number(t))) ? 'NUMERIC' : 'STRING'; })()
      : 'NUMERIC';

    const body = {
      ...rule,
      id: rule.id,
      name: editName.trim(),
      configuration: {
        ...rule.configuration,
        arguments: {
          [editSelectedKey]: {
            refEntityKey: { type: 'TS_LATEST', key: editSelectedKey },
            defaultValue: '',
          },
        },
        createRules: {
          [editSeverity]: {
            condition: {
              expression: {
                type: 'SIMPLE',
                operation: 'AND',
                filters: [{
                  argument: editSelectedKey,
                  valueType: saveValueType,
                  predicates,
                  operation: editCondition === 'INSIDE' ? 'AND' : 'OR',
                }],
              },
              type: 'SIMPLE',
            },
            alarmDetails: null,
            dashboardId: null,
          },
        },
      },
    };

    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/calculatedField`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht gespeichert werden (HTTP ${res.status})`);
      }
      const updated = await res.json();
      setRule(updated);
      setEditing(false);
      showStatus('Alarmregel wurde gespeichert.');
    } catch (err) {
      showStatus(`Alarmregel konnte nicht gespeichert werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setSaving(false);
    }
  }

  const editIsSingleThreshold = editCondition === 'ABOVE' || editCondition === 'BELOW';
  const editIsStateCondition = editCondition === 'STATE';
  const thresholdOrderValid = editIsSingleThreshold || editIsStateCondition ||
    editLowerThreshold.trim() === '' || editUpperThreshold.trim() === '' ||
    Number(editLowerThreshold) < Number(editUpperThreshold);
  const editIsValid = editName.trim() !== '' && editSeverity !== '' && editSelectedKey !== '' &&
    (editIsSingleThreshold
      ? editThreshold.trim() !== ''
      : editIsStateCondition
        ? editStateValue.trim() !== ''
        : editLowerThreshold.trim() !== '' && editUpperThreshold.trim() !== '') &&
    thresholdOrderValid;
  const editSeverityError = !editSeverity;
  const editSeverityErrorText = editSeverityText.trim()
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';
  const editKeyError = !editSelectedKey;
  const editKeyErrorText = editKeyText.trim()
    ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.'
    : 'Dieses Feld ist erforderlich.';
  const editOrig = editOriginalRef.current;
  const hasChanges = editing && editOrig != null && (
    editName !== editOrig.name ||
    editSeverity !== editOrig.severity ||
    editSelectedKey !== editOrig.key ||
    editCondition !== editOrig.condition ||
    editThreshold !== editOrig.threshold ||
    editLowerThreshold !== editOrig.lower ||
    editUpperThreshold !== editOrig.upper ||
    editStateValue !== editOrig.stateValue
  );


  const blocker = useBlocker(editing && hasChanges);


  useFocusTrap(blocker.state === 'blocked', blockerModalRef);


  useEffect(() => {
    function onBeforeUnload(e) {
      if (editing && hasChanges) { e.preventDefault(); e.returnValue = ''; return ''; }
      return undefined;
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editing, hasChanges]);

  if (loading) return <p style={{ margin: '24px 40px' }} aria-busy="true">Alarmregel wird geladen…</p>;
  if (error) return <div role="alert" style={{ margin: '24px 40px' }}>{error}</div>;
  if (!rule) return null;

  const severities = getSeverities(rule);
  const entityType = rule.entityId?.entityType;
  const entityId = rule.entityId?.id;
  const conditions = getConditionInfo(rule);

  const historyTotalPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const pagedHistory = history.slice(historyPage * HISTORY_PAGE_SIZE, (historyPage + 1) * HISTORY_PAGE_SIZE);
  const dataKeys = [...new Set(conditions.map(c => c.key))];
  const thresholdLabels = conditions.map(c => c.label);

  return (
    <div className="device-detail-page alarmrule-detail-page">
      {/* Header */}
      <div className="device-detail-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Zurück zur vorherigen Seite"
            title="Zurück zur vorherigen Seite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 id="alarmrule-title" tabIndex={-1} ref={titleRef} className="device-title">
            {rule.name}
            {severities.map(s => (
              <span key={s} className={`severity-badge ${s.toLowerCase()}`}>{severityLabels[s] ?? s}</span>
            ))}
          </h1>
        </div>

        <div className="device-actions">
          <button
            className="action-buttons"
            aria-label="Alarmregel löschen"
            title="Alarmregel löschen"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <div className="action-button-icon"><Löschen /></div>
            <div>Alarmregel löschen</div>
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Content Grid */}
      <main id="main-content" role="main" aria-labelledby="alarmrule-title" className="device-detail-grid">
        {/* Details Panel */}
        <div className="card-wrapper">
          <h2 className="card-title">Details</h2>
          <section className={`card card-details${editing ? ' editing' : ''}`}>
            {!editing ? (
              <>
                <dl>
                  <dt>Bezeichnung:</dt>
                  <dd>{rule.name}</dd>

                  <dt>Schweregrad:</dt>
                  <dd>
                    {severities.length > 0
                      ? severities.map(s => severityLabels[s] ?? s).join(', ')
                      : '—'}
                  </dd>

                  <dt>Bezug:</dt>
                  <dd>{entityTypeLabels[entityType] ?? entityType ?? '—'}</dd>

                  <dt>{entityType === 'DEVICE' ? 'Gerät' : 'Profil'}:</dt>
                  <dd>
                    {entityType === 'DEVICE' && entityId ? (
                      <button
                        type="button"
                        className="originator-link"
                        onClick={() => navigate(`/devices/${entityId}`, { state: { name: entityDisplayName } })}
                      >
                        {entityDisplayName || '—'}
                      </button>
                    ) : (
                      entityDisplayName || rule.entityName || '—'
                    )}
                  </dd>

                  <dt>Datenkey:</dt>
                  <dd>
                    {dataKeys.length > 0
                      ? dataKeys.join(', ')
                      : '—'}
                  </dd>

                  <dt>Auslösebedingung:</dt>
                  <dd>
                    {thresholdLabels.length > 0
                      ? thresholdLabels.join(', ')
                      : '—'}
                  </dd>

                  <dt>Erstellt:</dt>
                  <dd>{formatDateDE(rule.createdTime)}</dd>
                </dl>

                <button
                  className="edit"
                  aria-label="Alarmregel bearbeiten"
                  title="Alarmregel bearbeiten"
                  onClick={startEditing}
                >
                  <span className="icon" aria-hidden="true"><Bearbeiten /></span> Bearbeiten
                </button>
              </>
            ) : (
              <div className="edit-form">
                {/* Name */}
                <div className={`edit-form-group${editName !== editOriginalRef.current?.name ? ' changed' : ''}`}>
                  <label className="edit-form-label" htmlFor="edit-name">
                    Bezeichnung<span className="required">*</span>
                    {editName !== editOriginalRef.current?.name && <span className="changed-badge" aria-label="Geändert">• geändert</span>}
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="z. B. Temperatur zu hoch"
                    required
                  />
                </div>

                {/* Schweregrad */}
                <div className="search-dropdown-group">
                  <SearchDropdown
                    id="edit-severity"
                    label="Schweregrad"
                    required
                    placeholder="Schweregrad wählen"
                    inputValue={editSeverityText}
                    onInputChange={text => { setEditSeverityText(text); setEditSeverity(''); }}
                    options={SEVERITY_OPTIONS}
                    onSelect={o => { setEditSeverity(o.value); setEditSeverityText(o.label); }}
                    onClear={() => { setEditSeverity(''); setEditSeverityText(''); }}
                    changed={editSeverity !== editOriginalRef.current?.severity}
                  />
                  {editSeverityError && (
                    <p className="required-fields-error" role="alert">{editSeverityErrorText}</p>
                  )}
                </div>

                {/* Bezug (readonly) */}
                <div className="edit-form-group">
                  <label className="edit-form-label">Bezug</label>
                  <input type="text" value={entityTypeLabels[entityType] ?? entityType ?? '—'} disabled />
                </div>

                {/* Gerät/Profil (readonly) */}
                <div className="edit-form-group">
                  <label className="edit-form-label">{entityType === 'DEVICE' ? 'Gerät' : 'Profil'}</label>
                  <input type="text" value={entityDisplayName || rule.entityName || '—'} disabled />
                </div>

                {/* Datenkey */}
                <div className="search-dropdown-group">
                  <SearchDropdown
                    id="edit-datakey"
                    label="Datenkey"
                    required
                    placeholder="Datenkey wählen"
                    inputValue={editKeyText}
                    onInputChange={text => { setEditKeyText(text); setEditSelectedKey(''); }}
                    options={[
                      ...(editSelectedKey && !editDataKeys.includes(editSelectedKey) ? [{ value: editSelectedKey, label: editSelectedKey }] : []),
                      ...editDataKeys.map(k => ({ value: k, label: k }))
                    ]}
                    onSelect={o => { setEditSelectedKey(o.value); setEditKeyText(o.label); }}
                    onClear={() => { setEditSelectedKey(''); setEditKeyText(''); }}
                    changed={editSelectedKey !== editOriginalRef.current?.key}
                  />
                  {editKeyError && (
                    <p className="required-fields-error" role="alert">{editKeyErrorText}</p>
                  )}
                </div>

                {/* Auslösebedingung */}
                <div className="edit-checkbox-section">
                  <h3 className="edit-section-title">
                    Auslösebedingung
                    {editCondition !== editOriginalRef.current?.condition && <span className="changed-badge" aria-label="Geändert">&bull; geändert</span>}
                  </h3>
                  <p className="edit-section-subtitle">Alarm auslösen, wenn der Wert …</p>
                  <div className="edit-checkbox-group edit-checkbox-group--column" role="radiogroup" aria-label="Auslösebedingung">
                    <label>
                      <input type="radio" name="edit-condition" checked={editCondition === 'ABOVE'} onChange={() => { setEditCondition('ABOVE'); setEditThreshold(''); setEditLowerThreshold(''); setEditUpperThreshold(''); setEditStateValue(''); }} />
                      einen Grenzwert überschreitet
                    </label>
                    <label>
                      <input type="radio" name="edit-condition" checked={editCondition === 'BELOW'} onChange={() => { setEditCondition('BELOW'); setEditThreshold(''); setEditLowerThreshold(''); setEditUpperThreshold(''); setEditStateValue(''); }} />
                      einen Grenzwert unterschreitet
                    </label>
                    <label>
                      <input type="radio" name="edit-condition" checked={editCondition === 'OUTSIDE'} onChange={() => { setEditCondition('OUTSIDE'); setEditThreshold(''); setEditLowerThreshold(''); setEditUpperThreshold(''); setEditStateValue(''); }} />
                      außerhalb eines Bereichs liegt
                    </label>
                    <label>
                      <input type="radio" name="edit-condition" checked={editCondition === 'INSIDE'} onChange={() => { setEditCondition('INSIDE'); setEditThreshold(''); setEditLowerThreshold(''); setEditUpperThreshold(''); setEditStateValue(''); }} />
                      innerhalb eines Bereichs liegt
                    </label>
                    <label>
                      <input type="radio" name="edit-condition" checked={editCondition === 'STATE'} onChange={() => { setEditCondition('STATE'); setEditThreshold(''); setEditLowerThreshold(''); setEditUpperThreshold(''); setEditStateValue(''); }} />
                      einem Zustand entspricht
                    </label>
                  </div>
                </div>

                {/* Einzelner Grenzwert */}
                {editIsSingleThreshold && (
                  <div className={`edit-form-group${editThreshold !== editOriginalRef.current?.threshold ? ' changed' : ''}`}>
                    <label className="edit-form-label" htmlFor="edit-threshold">
                      Grenzwert<span className="required">*</span>
                      {editThreshold !== editOriginalRef.current?.threshold && <span className="changed-badge" aria-label="Geändert">&bull; geändert</span>}
                    </label>
                    <input
                      type="number"
                      id="edit-threshold"
                      value={editThreshold}
                      onChange={e => setEditThreshold(e.target.value)}
                      placeholder="z. B. 20"
                    />
                  </div>
                )}

                {/* Bereichsgrenzen */}
                {!editIsSingleThreshold && !editIsStateCondition && (
                  <div className="edit-threshold-row">
                    <div className={`edit-form-group${editLowerThreshold !== editOriginalRef.current?.lower ? ' changed' : ''}`}>
                      <label className="edit-form-label" htmlFor="edit-lower">
                        Unterer Grenzwert<span className="required">*</span>
                        {editLowerThreshold !== editOriginalRef.current?.lower && <span className="changed-badge" aria-label="Geändert">&bull; geändert</span>}
                      </label>
                      <input
                        type="number"
                        id="edit-lower"
                        value={editLowerThreshold}
                        onChange={e => setEditLowerThreshold(e.target.value)}
                        placeholder="z. B. 0"
                      />
                    </div>
                    <div className={`edit-form-group${editUpperThreshold !== editOriginalRef.current?.upper ? ' changed' : ''}`}>
                      <label className="edit-form-label" htmlFor="edit-upper">
                        Oberer Grenzwert<span className="required">*</span>
                        {editUpperThreshold !== editOriginalRef.current?.upper && <span className="changed-badge" aria-label="Geändert">&bull; geändert</span>}
                      </label>
                      <input
                        type="number"
                        id="edit-upper"
                        value={editUpperThreshold}
                        onChange={e => setEditUpperThreshold(e.target.value)}
                        placeholder="z. B. 100"
                      />
                    </div>
                  </div>
                )}

                {/* Zustandswert */}
                {editIsStateCondition && (
                  <div className={`edit-form-group${editStateValue !== editOriginalRef.current?.stateValue ? ' changed' : ''}`}>
                    <label className="edit-form-label" htmlFor="edit-state">
                      Zustand<span className="required">*</span>
                      {editStateValue !== editOriginalRef.current?.stateValue && <span className="changed-badge" aria-label="Geändert">&bull; geändert</span>}
                    </label>
                    <input
                      type="text"
                      id="edit-state"
                      value={editStateValue}
                      onChange={e => setEditStateValue(e.target.value)}
                      placeholder="z.B. true"
                    />
                  </div>
                )}

                {!thresholdOrderValid && (
                  <p className="required-hint" role="alert" style={{ color: '#b00020' }}>Der untere Grenzwert muss kleiner als der obere sein.</p>
                )}

                {!editIsValid && !editSeverityError && !editKeyError && (
                  <p className="required-fields-error" role="alert">Bitte fülle alle Pflichtfelder aus.</p>
                )}

                <p className="required-hint">* Pflichtfeld</p>

                <div className="edit-form-actions">
                  <button className="btn-primary" onClick={saveEdit} disabled={saving || !hasChanges}>
                    <Speichern /> {saving ? 'Speichern…' : 'Speichern'}
                  </button>
                  <button className="btn-secondary" onClick={cancelEditing} disabled={saving}>
                    <Abbrechen /> Abbrechen
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Historie Panel */}
        <div className="card-wrapper">
          <h2 className="card-title">Historie ({history.length})</h2>
          <section className="card alarmrule-history-card" aria-busy={historyLoading}>
            {historyLoading ? (
              <p aria-busy="true">Alarme werden geladen…</p>
            ) : history.length === 0 ? (
              <p className="no-history">Keine ausgelösten Alarme für diese Regel vorhanden.</p>
            ) : (
              <>
              <div className="alarmrule-history-list" role="list" aria-label="Alarm-Historie">
                {pagedHistory.map(a => (
                  <div key={a.id?.id} className="list-item alarm-item" role="listitem">
                    <div className="alarm-main">
                      <span className="alarm-date">{formatDateDE(a.createdTime)}</span>
                      <span className={`severity-badge ${(a.severity || '').toLowerCase()}`}>
                        {severityLabels[a.severity] ?? a.severity}
                      </span>
                    </div>

                    <div className="actions">
                      <AlarmStatusDropdown
                        alarmId={a.id?.id}
                        status={a.status}
                        isOpen={openAlarmStatusMenu === a.id?.id}
                        onToggle={() => setOpenAlarmStatusMenu(openAlarmStatusMenu === a.id?.id ? null : a.id?.id)}
                        onAck={() => updateAlarmStatus(a, 'ack', 'ACTIVE_ACK')}
                        onClear={() => updateAlarmStatus(a, 'clear', 'CLEARED_ACK')}
                        onClose={() => setOpenAlarmStatusMenu(null)}
                      />

                      <button
                        className="icon-button delete"
                        title="Alarm löschen"
                        aria-label="Alarm löschen"
                        onClick={() => { setAlarmToDelete(a); setShowAlarmDeleteConfirm(true); }}
                      >
                        <span aria-hidden="true"><Löschen /></span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {history.length > 0 && (
                <Pagination
                  page={historyPage}
                  totalPages={historyTotalPages}
                  totalItems={history.length}
                  pageSize={HISTORY_PAGE_SIZE}
                  onPageChange={setHistoryPage}
                  label="Alarme"
                />
              )}
              </>
            )}
          </section>
        </div>
      </main>

      {/* Alarm Delete Confirmation Modal */}
      {showAlarmDeleteConfirm && alarmToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-alarm-title">
          <div className="modal" ref={alarmDeleteConfirmRef}>
            <h2 id="delete-alarm-title">Alarm löschen?</h2>
            <p>
              Soll dieser Alarm wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>.
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={confirmDeleteAlarm} disabled={deletingAlarm}>
                {deletingAlarm ? 'Löschen…' : <><Löschen /> Löschen bestätigen</>}
              </button>
              <button className="modal-secondary" onClick={() => { setShowAlarmDeleteConfirm(false); setAlarmToDelete(null); }} disabled={deletingAlarm}>
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-rule-title">
          <div className="modal" ref={deleteConfirmRef}>
            <h2 id="delete-rule-title">Alarmregel löschen?</h2>
            <p>
              Soll die Alarmregel <i>{rule.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>.
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={deleteRule} disabled={deleting}>
                {deleting ? 'Löschen…' : <><Löschen /> Löschen bestätigen</>}
              </button>
              <button className="modal-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes confirmation modal (route blocker) */}
      {blocker.state === 'blocked' && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="unsaved-title">
          <div className="modal" ref={blockerModalRef}>
            <h2 id="unsaved-title">Ungespeicherte Änderungen</h2>
            <p>Es gibt ungespeicherte Änderungen. Möchtest du die Seite wirklich verlassen?</p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={() => { setEditing(false); blocker.proceed(); }}>
                Seite verlassen
              </button>
              <button className="modal-secondary" onClick={() => blocker.reset()}>
                ✕ Zurück
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AlarmruleDetailWithBoundary() {
  return <ErrorBoundary><AlarmruleDetail /></ErrorBoundary>;
}
